import { ref, computed, watch, toValue, type MaybeRefOrGetter } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { tryOnScopeDispose, useDocumentVisibility, useFetch, useIntervalFn } from '@vueuse/core'
import useAuthStore from '@/stores/authStore.ts'
import { type TokenList } from '@/stores/authStore.ts'
import { isEveSingleErrorResponseBody } from '@/types/_general'

export type BankIdMode = 'identification' | 'signing'

export interface BankIdInitResponse {
  token: string
  qr?: string
  auto_start_token?: string
}

export interface BankIdCreateSigningTokenResponse {
  token: string
}

export interface BankIdPollResponse {
  token?: string
  status: 'pending' | 'completed' | 'failed'
  qr?: string
  hint_code?: string
  token_list?: TokenList
  signing_token?: string
}

export interface BankIdCompletedData extends Omit<BankIdPollResponse, 'status'> {
  status: 'completed'
}

export interface BankIdOptions<SignData = unknown> {
  signing?: {
    data: SignData
    message?: string
  }
  endpointOverrides?: {
    init?: string
    poll?: string
    cancel?: string
    createSigningToken?: string
  }
}

const baseURL = import.meta.env.VITE_APP_API_ROOT

const sessionStorageKeys = {
  signingToken: 'bankid_signing_token',
  preAuthToken: 'bankid_pre_auth_token',
} as const

/**
 * Replaces URL placeholders like {signing_token} with actual values.
 * Returns null if any placeholder remains unresolved.
 */
function applyParamsToUrl(url: string, params: Record<string, string | undefined>): string | null {
  let resolvedUrl = url
  for (const [key, value] of Object.entries(params)) {
    const placeholder = `{${key}}`
    if (resolvedUrl.includes(placeholder)) {
      if (value == null) return null
      resolvedUrl = resolvedUrl.replaceAll(placeholder, value)
    }
  }
  return resolvedUrl
}

/**
 * Composable for BankID authentication supporting both identification (login) and signing modes.
 * Handles the full authentication flow: init → poll → completion, with support for
 * same-device redirects and custom endpoints.
 */
export function useBankId<SignData = unknown>(mode: BankIdMode, options?: MaybeRefOrGetter<BankIdOptions<SignData>>) {
  const resolvedOptions = computed(() => toValue(options) ?? {})
  const signing = computed(() => resolvedOptions.value.signing)
  const endpointOverrides = computed(() => resolvedOptions.value.endpointOverrides)

  const authStore = useAuthStore()
  const route = useRoute()
  const router = useRouter()

  const signingToken = ref<string | undefined>()

  const endpoints = computed(() => {
    const isSigning = mode === 'signing'

    const defaults = {
      createSigningToken: '/mobile-bankid--signs/create-signing-token',
      init: isSigning ? '/mobile-bankid--signs' : '/authenticates/mobile-bankid-init/{application}',
      poll: isSigning ? '/mobile-bankid--signs/{signing_token}' : '/authenticates/mobile-bankid',
      cancel: isSigning ? '/mobile-bankid--signs/{signing_token}' : '/authenticates/mobile-bankid',
    }

    const params = {
      application: import.meta.env.VITE_APP_NAME,
      signing_token: signingToken.value,
    }

    const createSigningTokenPath = endpointOverrides.value?.createSigningToken ?? defaults.createSigningToken
    const initPath = applyParamsToUrl(endpointOverrides.value?.init ?? defaults.init, params)
    const pollPath = applyParamsToUrl(endpointOverrides.value?.poll ?? defaults.poll, params)
    const cancelPath = applyParamsToUrl(endpointOverrides.value?.cancel ?? defaults.cancel, params)

    return {
      createSigningToken: new URL(createSigningTokenPath, baseURL).href,
      init: initPath != null ? new URL(initPath, baseURL).href : '',
      poll: pollPath != null ? new URL(pollPath, baseURL).href : '',
      cancel: cancelPath != null ? new URL(cancelPath, baseURL).href : '',
    }
  })

  const createSigningTokenRequest = useFetch<BankIdCreateSigningTokenResponse>(
    () => endpoints.value.createSigningToken,
    {
      updateDataOnError: true,
      immediate: false,
      beforeFetch(ctx) {
        const token = authStore.selectedToken?.token
        if (token != null) {
          ctx.options.headers = { 'x-api-key': token }
        }
      },
    }
  )
    .post(computed(() => signing.value?.data))
    .json<BankIdCreateSigningTokenResponse>()

  const createSigningData = computed(() =>
    isEveSingleErrorResponseBody(createSigningTokenRequest.data.value)
      ? undefined
      : createSigningTokenRequest.data.value
  )
  const createError = computed(() =>
    isEveSingleErrorResponseBody(createSigningTokenRequest.data.value)
      ? createSigningTokenRequest.data.value
      : undefined
  )

  const initBody = computed(() => {
    if (mode === 'signing') {
      return {
        signing_token: signingToken.value,
        user_visible_data: signing.value?.message,
      }
    }
    return {}
  })

  const initRequest = useFetch<BankIdInitResponse>(() => endpoints.value.init, {
    updateDataOnError: true,
    immediate: false,
    beforeFetch(ctx) {
      if (mode === 'signing') {
        const token = authStore.selectedToken?.token
        if (token != null) {
          ctx.options.headers = { 'x-api-key': token }
        }
      }
    },
  })
    .post(initBody)
    .json<BankIdInitResponse>()

  const initData = computed(() =>
    isEveSingleErrorResponseBody(initRequest.data.value) ? undefined : initRequest.data.value
  )
  const initError = computed(() =>
    isEveSingleErrorResponseBody(initRequest.data.value) ? initRequest.data.value : undefined
  )
  const hasInitData = computed(() => !!initData.value && !initError.value)

  const restoredPreAuthToken = ref<string | undefined>()

  const preAuthToken = computed(() => restoredPreAuthToken.value ?? pollData.value?.token ?? initData.value?.token)

  const pollRequest = useFetch<BankIdPollResponse>(() => endpoints.value.poll, {
    updateDataOnError: true,
    timeout: 5_000,
    immediate: false,
    beforeFetch(ctx) {
      const tokenToUse = mode === 'signing' ? authStore.selectedToken?.token : preAuthToken.value
      if (tokenToUse == null) {
        ctx.cancel()
        return
      }

      ctx.options.headers = {
        'x-api-key': tokenToUse,
        'cache-control': 'no-cache',
      }
    },
  })
    .get()
    .json<BankIdPollResponse>()

  const pollData = computed(() =>
    isEveSingleErrorResponseBody(pollRequest.data.value) ? undefined : pollRequest.data.value
  )
  const pollError = computed(() =>
    isEveSingleErrorResponseBody(pollRequest.data.value) ? pollRequest.data.value : undefined
  )
  const canPoll = computed(() => {
    if (mode === 'signing') {
      return signingToken.value != null && authStore.selectedToken?.token != null
    }
    return preAuthToken.value != null
  })

  const pollInterval = useIntervalFn(() => {
    if (pollRequest.isFetching.value) return
    if (hasInitData.value && canPoll.value) {
      void pollRequest.execute()
    }
  }, 1_000)

  const status = computed(() => pollData.value?.status)
  const error = computed(() => createError.value ?? pollError.value ?? initError.value)
  const documentVisibility = useDocumentVisibility()

  watch(
    [() => createSigningData.value?.token, () => pollData.value?.token, () => initData.value?.token],
    ([createToken, pollToken, initToken]) => {
      signingToken.value = initToken ?? pollToken ?? createToken
    },
    { immediate: true }
  )

  watch(
    [canPoll, documentVisibility, status, error, hasInitData],
    ([canPollNow, visibility, currentStatus, currentError, hasInitData]) => {
      if (
        canPollNow &&
        hasInitData &&
        visibility === 'visible' &&
        (currentStatus === 'pending' || currentStatus == null) &&
        currentError == null
      ) {
        pollInterval.resume()
      } else {
        pollInterval.pause()
      }
    }
  )

  const completedData = ref<BankIdCompletedData | undefined>()

  watch(status, async (newStatus) => {
    if (newStatus === 'completed') {
      if (pollData.value?.token_list != null) {
        authStore.setTokenList(pollData.value.token_list)
      }

      if (mode === 'identification') {
        const returnTo =
          typeof route.query['return-to'] === 'string' ? decodeURIComponent(route.query['return-to']) : '/'
        await router.replace(returnTo)
      } else {
        completedData.value = {
          ...pollData.value,
          status: 'completed',
        }
      }
    }
  })

  async function start() {
    reset()

    if (mode === 'signing') {
      await createSigningTokenRequest.execute()
      const createdToken = createSigningData.value?.token
      if (!createdToken) return
      signingToken.value = createdToken
      return initRequest.execute()
    }
    return initRequest.execute()
  }

  function reset() {
    pollInterval.pause()

    if (createSigningTokenRequest.canAbort.value) createSigningTokenRequest.abort()
    if (initRequest.canAbort.value) initRequest.abort()
    if (pollRequest.canAbort.value) pollRequest.abort()

    createSigningTokenRequest.data.value = null
    initRequest.data.value = null
    pollRequest.data.value = null

    signingToken.value = undefined
    restoredPreAuthToken.value = undefined
    completedData.value = undefined
  }

  tryOnScopeDispose(() => {
    reset()
  })

  const cancelRequest = useFetch(() => endpoints.value.cancel, {
    immediate: false,
    onFetchError(ctx) {
      console.error('Error during BankID cancel request:', ctx.error)
      return ctx
    },
    beforeFetch(ctx) {
      const tokenToUse = mode === 'signing' ? authStore.selectedToken?.token : preAuthToken.value
      if (tokenToUse == null) {
        ctx.cancel()
        return
      }
      ctx.options.headers = {
        'x-api-key': tokenToUse,
        'cache-control': 'no-cache',
      }
    },
  })
    .delete()
    .json()

  const cancelLogin = async () => {
    if (endpoints.value.cancel) await cancelRequest.execute()
    sessionStorage.removeItem(sessionStorageKeys.signingToken)
    sessionStorage.removeItem(sessionStorageKeys.preAuthToken)
  }

  function prepareForRedirect() {
    if (mode === 'signing') {
      if (signingToken.value) {
        sessionStorage.setItem(sessionStorageKeys.signingToken, signingToken.value)
      }
    } else {
      if (preAuthToken.value) {
        sessionStorage.setItem(sessionStorageKeys.preAuthToken, preAuthToken.value)
      }
    }
    pollInterval.pause()
  }

  async function ensureTokenOnLoad() {
    try {
      if (mode === 'signing') {
        const storedToken = sessionStorage.getItem(sessionStorageKeys.signingToken)
        if (storedToken) {
          signingToken.value = storedToken
          sessionStorage.removeItem(sessionStorageKeys.signingToken)
          await pollRequest.execute()
        }
      } else {
        const storedToken = sessionStorage.getItem(sessionStorageKeys.preAuthToken)
        if (storedToken) {
          restoredPreAuthToken.value = storedToken
          sessionStorage.removeItem(sessionStorageKeys.preAuthToken)
          await pollRequest.execute()
        }
      }
    } catch (err) {
      signingToken.value = undefined
      restoredPreAuthToken.value = undefined
      console.error('Error during BankID token restoration:', err)
    }
  }
  void ensureTokenOnLoad()

  return {
    start,
    reset,
    cancelLogin,
    prepareForRedirect,
    status,
    error,
    qrBase64: computed(() => pollData.value?.qr ?? initData.value?.qr),
    autostartToken: computed(() => initData.value?.auto_start_token),
    hintCode: computed(() => pollData.value?.hint_code),
    signingToken: computed(() => signingToken.value),
    completedData: computed(() => completedData.value),
    isPolling: pollRequest.isFetching,
    hasActiveSession: computed(() => hasInitData.value && canPoll.value),
  }
}
